/**
 * LERA Smart Selector — Premium Dropdown UI (Sprint 8.3C.2 + 8.3C.2A)
 * Presentation layer only. State/events owned by <lera-smart-selector>.
 */

const LERA_SS_VIEWPORT_PAD = 12;

function leraSsGetAnimMs(host, closing) {
  const styles = getComputedStyle(host);
  const key = closing ? '--lera-ss-anim-close' : '--lera-ss-anim-open';
  const raw = styles.getPropertyValue(key).trim();
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : closing ? 160 : 180;
}

if (!customElements.get('lera-smart-dropdown')) {
  customElements.define(
    'lera-smart-dropdown',
    class LeraSmartDropdown extends HTMLElement {
      constructor() {
        super();
        this.isOpen = false;
        this.isAnimating = false;
        this.activeIndex = -1;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        this.nativeSelect = this.closest('.lera-smart-selector__field')?.querySelector('.lera-smart-selector__native-select');
        this.trigger = this.querySelector('.lera-smart-dropdown__trigger');
        this.panel = this.querySelector('.lera-smart-dropdown__panel');
        this.list = this.querySelector('.lera-smart-dropdown__list');
        this.valueEl = this.querySelector('[data-selected-value]');
        this.optionEls = Array.from(this.querySelectorAll('.lera-smart-dropdown__option'));

        if (!this.nativeSelect || !this.trigger || !this.panel) return;

        this.trigger.addEventListener('click', () => this.toggle());
        this.list?.addEventListener('click', (event) => this.onListClick(event));
        this.list?.addEventListener('mousemove', (event) => {
          const option = event.target.closest('.lera-smart-dropdown__option');
          if (!option || option.classList.contains('is-disabled')) return;
          this.setActiveOption(option, false);
        });

        this.syncFromSelect();
      }

      disconnectedCallback() {
        this.close(false);
      }

      getEnabledOptions() {
        return this.optionEls.filter((option) => !option.classList.contains('is-disabled'));
      }

      syncFromSelect() {
        if (!this.nativeSelect) return;
        const selectedOption = this.nativeSelect.selectedOptions[0];
        const value = selectedOption?.value || '';

        this.optionEls.forEach((option) => {
          const isSelected = option.dataset.value === value;
          if (option.classList.contains('is-selected') === isSelected) return;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });

        if (this.valueEl && selectedOption) {
          const nextLabel = selectedOption.textContent.trim();
          if (this.valueEl.textContent !== nextLabel) {
            this.valueEl.textContent = nextLabel;
          }
        }

        const enabled = this.getEnabledOptions();
        this.activeIndex = Math.max(
          0,
          enabled.findIndex((option) => option.dataset.value === value)
        );
      }

      toggle() {
        if (this.isOpen) {
          this.close(true);
        } else {
          this.open();
        }
      }

      open() {
        if (this.isOpen || this.isAnimating) return;
        this.isOpen = true;
        this.isAnimating = true;
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        this.mountPanel();
        this.positionPanel();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.panel.classList.add('is-visible');
            window.setTimeout(() => {
              this.isAnimating = false;
            }, leraSsGetAnimMs(this, false));
          });
        });
        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('keydown', this.onDocumentKeydown, true);
        window.addEventListener('resize', this.onResize, { passive: true });
        window.addEventListener('scroll', this.onResize, true);
      }

      close(animate = true) {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        document.removeEventListener('click', this.onDocumentClick, true);
        document.removeEventListener('keydown', this.onDocumentKeydown, true);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onResize);

        if (!animate) {
          this.panel?.classList.remove('is-visible');
          if (this.panel) this.panel.hidden = true;
          this.unmountPanel();
          return;
        }

        this.isAnimating = true;
        this.panel?.classList.remove('is-visible');
        window.setTimeout(() => {
          if (this.panel) this.panel.hidden = true;
          this.unmountPanel();
          this.isAnimating = false;
        }, leraSsGetAnimMs(this, true));
      }

      mountPanel() {
        if (!this.panel || this.panelPortaled) return;
        this.panelPlaceholder = document.createComment('lera-smart-dropdown-panel');
        this.panel.before(this.panelPlaceholder);
        document.body.appendChild(this.panel);
        this.panelPortaled = true;
        this.panel.classList.add('lera-smart-dropdown__panel--floating');
      }

      unmountPanel() {
        if (!this.panel || !this.panelPortaled || !this.panelPlaceholder?.parentNode) return;
        this.panelPlaceholder.parentNode.insertBefore(this.panel, this.panelPlaceholder);
        this.panelPlaceholder.remove();
        this.panelPlaceholder = null;
        this.panelPortaled = false;
        this.panel.classList.remove('lera-smart-dropdown__panel--floating');
      }

      positionPanel() {
        if (!this.panel || !this.trigger) return;
        const triggerRect = this.trigger.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const panelWidth = triggerRect.width;
        const left = Math.max(
          LERA_SS_VIEWPORT_PAD,
          Math.min(triggerRect.left, viewportWidth - panelWidth - LERA_SS_VIEWPORT_PAD)
        );

        this.panel.style.width = `${panelWidth}px`;
        this.panel.style.left = `${left}px`;
        this.panel.style.top = `${triggerRect.bottom + 8}px`;
      }

      onResize() {
        if (this.isOpen) this.positionPanel();
      }

      onDocumentClick(event) {
        if (!this.contains(event.target) && !this.panel?.contains(event.target)) {
          this.close(true);
        }
      }

      onDocumentKeydown(event) {
        if (!this.isOpen) return;

        const enabled = this.getEnabledOptions();
        if (!enabled.length) return;

        switch (event.key) {
          case 'Escape':
            event.preventDefault();
            this.close(true);
            this.trigger?.focus();
            break;
          case 'ArrowDown':
            event.preventDefault();
            this.activeIndex = (this.activeIndex + 1) % enabled.length;
            this.setActiveOption(enabled[this.activeIndex], true);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.activeIndex = (this.activeIndex - 1 + enabled.length) % enabled.length;
            this.setActiveOption(enabled[this.activeIndex], true);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (enabled[this.activeIndex]) {
              this.selectOption(enabled[this.activeIndex]);
            }
            break;
          case 'Tab':
            this.close(true);
            break;
          default:
            break;
        }
      }

      setActiveOption(option, scrollIntoView) {
        this.optionEls.forEach((el) => el.classList.remove('is-focused'));
        option.classList.add('is-focused');
        this.activeIndex = this.getEnabledOptions().indexOf(option);
        if (scrollIntoView) {
          option.scrollIntoView({ block: 'nearest' });
        }
      }

      onListClick(event) {
        const option = event.target.closest('.lera-smart-dropdown__option');
        if (!option || option.classList.contains('is-disabled')) return;
        this.selectOption(option);
      }

      selectOption(optionEl) {
        const select = this.nativeSelect;
        if (!select) return;

        const value = optionEl.dataset.value;
        const nativeOption = Array.from(select.options).find((opt) => opt.value === value);
        if (!nativeOption || nativeOption.disabled) return;
        if (select.value === value) {
          this.close(true);
          return;
        }

        select.value = value;
        Array.from(select.options).forEach((opt) => opt.removeAttribute('selected'));
        nativeOption.setAttribute('selected', 'selected');
        select.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncFromSelect();
        this.close(true);
        this.trigger?.focus();
      }
    }
  );
}
